import classNames from 'classnames'
import {useHydrating, useMergedRefs} from 'quickstart/hooks'
import {dataProps} from 'quickstart/theme'
import * as Render from 'quickstart/utils/render'
import * as R from 'rambdax'
import {type ComponentProps, type ElementType, useMemo, useRef} from 'react'
import {
  logger,
  simplifyHtml,
  simplifyTocHtml,
  stripScripts,
  stripTags,
} from 'tizra'
import S from './styles.module.scss'
import {useScriptRunner} from './useScriptRunner'

const log = logger('Html')

// exported for tests
export const variants = {
  customer: {
    simplify: simplifyHtml,
    strip: stripTags,
  },
  raw: {
    simplify: R.identity<string>,
    strip: stripTags,
  },
  dangerous: {
    simplify: R.identity<string>,
    strip: stripScripts,
  },
  snippet: {
    simplify: stripScripts,
    strip: stripTags,
  },
  strip: {
    simplify: stripTags,
    strip: stripTags,
  },
  toc: {
    simplify: simplifyTocHtml,
    strip: stripTags,
  },
}

type HtmlVariant = keyof typeof variants

const TagName = 'span' satisfies ElementType
type TagName = typeof TagName

interface HtmlProseOptions extends Render.Options {
  links?: 'footer'
}

type HtmlProseProps<T extends ElementType = TagName> = Render.Props<
  T,
  HtmlProseOptions
>

const HtmlProse = ({links, ...props}: HtmlProseProps) =>
  Render.createElement(TagName, {
    ...dataProps({links}),
    ...props,
    className: classNames(S['html-prose'], props.className),
  })

interface HtmlProps extends ComponentProps<typeof HtmlProse> {
  html?: string
  reset?: boolean
  variant?: HtmlVariant
  _eval?: any // for tests
}

export const Html = ({
  ref: forwardedRef,
  html: rawHtml,
  reset,
  variant = 'customer',
  _eval,
  children,
  ...props
}: HtmlProps) => {
  log.assert(!children || !rawHtml, 'received both children and html')

  const myRef = useRef<HTMLElement>(null)
  const mergedRef = useMergedRefs([forwardedRef, myRef])
  const stage = useHydrating() ? 'strip' : 'simplify'
  const cook = variants[variant][stage]
  const cookedHtml = useMemo<string>(() => cook(rawHtml || ''), [cook, rawHtml])
  const readyHtml = useScriptRunner({html: cookedHtml, ref: myRef, _eval})

  const dangerouslySetInnerHTML = useMemo(
    () => ({__html: readyHtml}),
    [readyHtml],
  )

  const contentProps = children ? {children} : {dangerouslySetInnerHTML}

  return (
    <HtmlProse
      {...props}
      className={classNames(
        reset && 'customer-reset',
        'customer-html',
        props.className,
      )}
      {...contentProps}
      ref={mergedRef}
    />
  )
}
